import { Paper, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Palette } from 'Palette';
import { PALETTE_DRAG_MIME } from './blockPaletteConstants';
import type { PaletteBlockDef } from './blockPaletteBlocks';
import { useBlockPaletteDrag } from './blockPaletteDragContext';
import MiniatureBlockPreview from './MiniatureBlockPreview';

interface PaletteBlockCardProps {
  block: PaletteBlockDef;
}

/**
 * A single draggable entry in the block palette: a non-interactive preview
 * graphic of the block above its label. Only the drag *source* is wired here —
 * the canvas drop target comes later.
 */
export default function PaletteBlockCard({ block }: PaletteBlockCardProps) {
  const { startDrag, endDrag } = useBlockPaletteDrag();
  const [dragging, setDragging] = useState(false);
  const preview = useMemo(() => block.renderPreview(), [block]);

  return (
    <Paper
      variant="outlined"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData(PALETTE_DRAG_MIME, block.type);
        event.dataTransfer.effectAllowed = 'copy';
        startDrag(block.type);
        setDragging(true);
      }}
      onDragEnd={() => {
        endDrag();
        setDragging(false);
      }}
      aria-label={`Drag to add ${block.label}`}
      sx={{
        p: 1,
        borderRadius: 2,
        cursor: 'grab',
        opacity: dragging ? 0.5 : 1,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': { boxShadow: 3, borderColor: Palette.default.buttonBg },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <MiniatureBlockPreview>{preview}</MiniatureBlockPreview>
      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.25, mt: 0.75 }}>
        {block.label}
      </Typography>
    </Paper>
  );
}
